import { Link, useNavigate } from 'react-router-dom'
import { CornerDownRight, ExternalLink, Pause, Repeat, ShieldCheck, Trash2 } from 'lucide-react'
import type { Skill, SkillBoardCard } from '../../services/skillService'
import { skillState } from '../../utils/skillSections'
import { levelWords, nextWhenWords } from '../../utils/skillWords'
import { lifeAreaLabel } from '../../utils/taskForm'
import { ActionMenu } from '../ui/ActionMenu'
import { Timestamp } from '../ui/Timestamp'
import { SkillLadder } from './SkillLadder'
import { Last14Practice } from './SkillKeepingUp'

/**
 * One row of the skill list (Skills UX pass 2026-09-06). The ladder leads —
 * where they are and where they want to get — then a title and one quiet
 * line: the next thing to practise (a task with its slot, or a habit's next
 * time), what got done lately, what it sits under. Nothing is marked on a
 * skill itself; the marks live on the tasks and habits under it.
 */
interface Props {
  skill: Skill
  card: SkillBoardCard | null | undefined
  showParent?: boolean
  showArea?: boolean
  timeHint?: 'created' | 'updated' | null
  tz?: string | null
  onDelete: (skill: Skill) => void
  diaryMode?: boolean
}

export function SkillRow({ skill, card, showParent = false, showArea = false, timeHint = null, tz, onDelete, diaryMode = false }: Props) {
  const navigate = useNavigate()
  const href = `/skills/${skill.id}`
  const state = skillState(skill)
  const resting = state.key === 'paused'          // parked is rationing's word; the skill stays live
  const enough = state.key === 'enough'
  const live = !resting && !enough
  const area = skill.life_area || skill.parent?.life_area || null
  const levels = levelWords(skill)
  const nextWhen = nextWhenWords(card?.next, tz)

  const menuItems = [
    { label: 'Open', icon: <ExternalLink className="h-4 w-4" />, onSelect: () => navigate(href), testId: 'skill-row-open' },
    { label: 'Delete', icon: <Trash2 className="h-4 w-4" />, onSelect: () => onDelete(skill), danger: true, testId: 'skill-row-delete' },
  ]

  // The ladder and its words together (owner, 2026-09-12: "add the caption").
  // Five dots alone read as decoration; "Developing → Proficient" beside them
  // says what they are. The words drop to the meta line on a phone, where the
  // lead column has no room for them.
  const lead = (
    <span className="inline-flex min-h-11 shrink-0 items-center justify-center gap-1.5 lg:min-h-8" aria-hidden={!live} data-testid="skill-row-lead">
      {enough ? <span className="inline-flex w-11 justify-center"><ShieldCheck className="h-4 w-4 text-ok" /></span>
        : resting ? <span className="inline-flex w-11 justify-center"><Pause className="h-4 w-4 text-muted" /></span>
        : (
          <>
            <span className="inline-flex w-11 justify-center"><SkillLadder current={skill.current_level} target={skill.target_level} size="sm" /></span>
            {/* Metadata by rule (North Star rule 4): the words or "no level yet" —
                never five silent dots. Every prod skill had no level on 2026-09-12. */}
            <span className="hidden whitespace-nowrap text-app-micro text-muted sm:inline" data-testid="skill-row-levels">{levels || 'no level yet'}</span>
          </>
        )}
    </span>
  )

  return (
    <div
      className={`group flex items-center gap-1.5 rounded-lg border px-1.5 py-1 transition-colors sm:gap-2 sm:px-2 ${
        diaryMode ? 'border-warn bg-warn/20 hover:bg-warn/30' : 'border-soft bg-surface hover:border-brand'
      } ${enough ? 'opacity-80' : ''}`}
      onClick={(e) => { if (!(e.target as HTMLElement).closest('a,button,[role=menu],[role=dialog]')) navigate(href) }}
      data-testid="skill-row"
      data-state={state.key}
    >
      {lead}
      <div className="min-w-0 flex-1 py-0.5">
        <div className="flex min-w-0 items-center gap-1.5">
          <Link to={href} onClick={(e) => e.stopPropagation()} className={`truncate font-semibold hover:text-brand ${live ? 'text-primary' : 'text-muted'} ${diaryMode ? 'font-handwritten text-base sm:text-lg' : 'text-app-body'}`} title={skill.description || undefined} data-testid="skill-row-title">
            {skill.title}
          </Link>
          {state.words && <span className="hidden shrink-0 rounded bg-surface-alt px-1.5 py-0.5 text-app-micro font-medium text-secondary sm:inline" data-testid="skill-row-state">{state.words}</span>}
          {card?.checkpoint && !state.words && <span className="shrink-0 rounded bg-brand-subtle px-1.5 py-0.5 text-app-micro font-medium text-brand" data-testid="skill-row-checkpoint">Ready to check</span>}
          {live && card?.today && <span className="shrink-0 text-app-micro font-medium text-ok" data-testid="skill-row-today">practised today</span>}
        </div>
        <div className="mt-0.5 flex min-w-0 items-center gap-x-2 overflow-hidden whitespace-nowrap text-app-micro text-muted sm:gap-x-2.5" data-testid="skill-row-meta">
          {state.words && <span className="shrink-0 sm:hidden">{state.words}</span>}
          {(levels || live) && <span className={`shrink-0 ${live ? 'sm:hidden' : ''}`}>{levels || 'no level yet'}</span>}
          {live && card?.next && (
            <span className="inline-flex min-w-0 items-center gap-1 truncate font-medium text-brand" title={card.next.title || undefined} data-testid="skill-row-next">
              {card.next.kind === 'habit' && <Repeat className="h-3 w-3 shrink-0" aria-hidden="true" />}
              next: {card.next.title}{nextWhen ? ` · ${nextWhen}` : ''}
            </span>
          )}
          {live && !card?.next && card?.words && <span className="shrink-0">{card.words}</span>}
          {live && card && card.days_active > 0 && <span className="hidden shrink-0 md:inline-flex"><Last14Practice days={card.last_14} size="sm" /></span>}
          {showParent && skill.parent?.title && (
            <span className="hidden min-w-0 items-center gap-1 truncate md:inline-flex" title={skill.parent.title}><CornerDownRight className="h-3 w-3 shrink-0" aria-hidden="true" />{skill.parent.title}</span>
          )}
          {showArea && area && <span className="hidden shrink-0 md:inline">{lifeAreaLabel(area)}</span>}
          {timeHint && <span className="shrink-0" data-testid="skill-row-time-hint">{timeHint === 'created' ? 'added' : 'updated'} <Timestamp value={timeHint === 'created' ? (skill.created_at || skill.updated_at || '') : (skill.updated_at || skill.created_at)} mode="datetime" /></span>}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-0.5">
        {live && !card?.path && !card?.next && (
          <Link to={href} onClick={(e) => e.stopPropagation()} className="hidden rounded-md px-2 py-1 text-app-micro font-medium text-brand hover:bg-brand-subtle lg:inline-flex" data-testid="skill-row-map">Plan the journey</Link>
        )}
        <ActionMenu compact label={`More for ${skill.title}`} items={menuItems} testId="skill-row-more" />
      </div>
    </div>
  )
}

export default SkillRow
